import React, { forwardRef } from "react";
import Draggable from "react-draggable";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogActions from "@material-ui/core/DialogActions";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import { CustomButton } from "../Button";
import { Store } from "../../middleware";
import { Loader } from "../Loader";
import { mobileSize, useCheckIsMobile } from "../../utils/responsive";
import { fontFamilies } from "../../constants";

const styles = (theme) => ({
  root: {
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "40px 40px 0 40px",
    [theme.breakpoints.down(mobileSize)]: {
      padding: 16,
    },
  },
  closeButton: {
    color: "#000",
    padding: 5,
    [theme.breakpoints.down(mobileSize)]: {
      padding: 0,
    },
  },
  title: ({ isBrand }) => ({
    fontSize: isBrand && 28,
    fontFamily: isBrand && fontFamilies[300],
  }),
});

type DialogTitleProps = {
  children: React.ReactNode;
  onClose?: () => void;
  closeCrossHidden?: boolean;
  isBrand?: boolean;
  closeCrossColor?: string;
  [key: string]: any;
};

const DialogTitle = withStyles(styles)(
  (props: DialogTitleProps & { classes: any }) => {
    const {
      children,
      classes,
      onClose,
      closeCrossHidden,
      isBrand,
      closeCrossColor,
      ...other
    } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h5" className={classes.title}>
          {children}
        </Typography>
        {onClose && !closeCrossHidden ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            className={classes.closeButton}
          >
            <CloseIcon
              color={
                closeCrossColor ? "inherit" : isBrand ? "inherit" : "primary"
              }
              style={closeCrossColor ? { color: closeCrossColor } : {}}
            />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  }
);

const DialogContent = withStyles((theme) => ({
  root: {
    padding: "0 40px 40px",
    position: "relative",
    // "& > div": {
    //   zIndex: "99999999999",
    //   width: "calc(100% - 75px)",
    // },
    // "&::-webkit-scrollbar": {
    //   display: "none",
    // },
    [theme.breakpoints.down(mobileSize)]: {
      padding: 16,
    },
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    padding: "0 40px 40px",
    flexBasis: "auto",
    maxWidth: "inherit",
    [theme.breakpoints.down(mobileSize)]: {
      padding: "0",
      margin: 20,
      marginTop: 10,
      flexBasis: "auto",
      maxWidth: "inherit",
    },
  },
}))(MuiDialogActions);

const useStyles = makeStyles(() => ({
  root: {
    "&::-webkit-scrollbar": {
      display: ({ withScroll }) => !withScroll && "none",
    },
  },
}));

const DraggablePaperComponent = React.forwardRef<HTMLDivElement, any>(
  function DraggablePaperComponent(props, ref) {
    const { PaperProps, ...rest } = props;
    return (
      <Draggable
        handle="#customized-dialog-title"
        cancel={
          '[class*="MuiDialogContent-root"],[class*="closeButton"],button[aria-label="close"]'
        }
      >
        <Paper ref={ref} {...rest} />
      </Draggable>
    );
  }
);

function AlertDialog({
  title,
  isOpen,
  handlePositiveActive,
  handleNegativeActive,
  handleClose,
  buttonText,
  cancelButtonText = "Cancel",
  content,
  fullWidth,
  fullScreenMobile,
  withoutCancelButton,
  closeCrossHidden,
  positiveIsDisabled,
  negativeIsDisabled,
  fullHeight = false,
  isBrand,
  dialogBackground = "white",
  closeCrossColor,
  positiveColor = "secondary",
  withScroll = true,
}) {
  const classes = useStyles({ withScroll, title });
  const isMobile = useCheckIsMobile();

  return (
    <Dialog
      fullWidth={fullWidth}
      fullScreen={fullScreenMobile && isMobile}
      open={Boolean(isOpen)}
      onClose={handleClose}
      PaperComponent={DraggablePaperComponent as unknown as React.ElementType}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="MuiPaper-paperFullHeight"
      PaperProps={{
        style: fullHeight
          ? { height: "100%", background: dialogBackground }
          : { background: dialogBackground },
      }}
    >
      <DialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
        closeCrossHidden={closeCrossHidden}
        isBrand={isBrand}
        closeCrossColor={closeCrossColor}
      >
        {title}
      </DialogTitle>
      {(content || (!content && !isMobile)) && (
        <Store.Consumer>
          {({ isLoading }) => (
            <>
              {isLoading && <Loader isModal={true} />}
              <DialogContent className={classes.root}>{content}</DialogContent>
            </>
          )}
        </Store.Consumer>
      )}
      {(handlePositiveActive || handleNegativeActive) && (
        <DialogActions
          style={{
            maxWidth: "initial",
            flexBasis: "auto",
          }}
        >
          {!withoutCancelButton && (
            <CustomButton color="default" onClick={handleClose}>
              {cancelButtonText}
            </CustomButton>
          )}
          {handlePositiveActive && (
            <CustomButton
              onClick={handlePositiveActive}
              disabled={positiveIsDisabled}
              color={positiveColor}
            >
              {buttonText}
            </CustomButton>
          )}
          {handleNegativeActive && (
            <CustomButton
              style={{
                backgroundColor: "#FF3200",
                color: "#fff",
              }}
              onClick={handleNegativeActive}
              disabled={negativeIsDisabled}
            >
              {buttonText}
            </CustomButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default AlertDialog;
